/**
 * 语言工具函数
 */

import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

/**
 * 获取当前系统语言
 * 从i18n实例获取，优先使用resolvedLanguage，如果没有则使用language
 * @returns 当前语言（如 'zh', 'en', 'es'），默认返回 'en'
 */
export function getCurrentLanguage(): string {
    try {
        // 从i18n实例获取当前语言
        // resolvedLanguage 是i18n检测并解析后的语言代码
        // language 是当前设置的语言代码
        const lang = i18n.resolvedLanguage || i18n.language || 'en';

        // 处理可能的格式如 'zh-CN'，只取前两位
        return lang.toLowerCase().split('-')[0] || 'en';
    } catch (e) {
        // 如果i18n未初始化或获取失败，尝试从localStorage获取
        const storedLang = localStorage.getItem('swiftx_language') || localStorage.getItem('i18nextLng');
        if (storedLang) {
            return storedLang.toLowerCase().split('-')[0] || 'en';
        }

        // 默认返回英文
        return 'en';
    }
}

/**
 * 判断当前语言是否为中文
 * @returns 如果是中文返回 true，否则返回 false
 */
export function isChineseLanguage(): boolean {
    return getCurrentLanguage() === 'zh';
}

/**
 * 判断当前运行环境是否为“外语环境”
 *
 * - 该函数是纯函数（非 Hook），可在工具类中使用
 * - 优先读取 document.documentElement.lang（如果存在）
 * - 兜底使用 i18n 当前语言
 */
export function isForeignLanguageEnv(): boolean {
    try {
        if (typeof window !== 'undefined') {
            const domLang = (document.documentElement.lang || '').toLowerCase();
            if (domLang) {
                return domLang !== 'zh-cn' && domLang !== 'zh';
            }
        }
    } catch (e) {
        // ignore
    }

    return !isChineseLanguage();
}

/**
 * 判断当前语言是否为需要垂直布局的语言（非中文）
 * 中文标签较短，适合水平布局；外语标签较长，适合垂直布局
 * 
 * ⚠️ 注意：此函数在组件中使用时，每次渲染都会调用，可能影响性能
 * 建议在 React 组件中使用 useShouldUseVerticalLayout Hook 替代
 * 
 * @returns 如果是非中文返回 true，否则返回 false
 */
export function shouldUseVerticalLayout(): boolean {
    return !isChineseLanguage();
}

/**
 * React Hook: 判断当前语言是否为需要垂直布局的语言（非中文）
 * 使用 useTranslation 和 useMemo 优化性能，只在语言变化时重新计算
 * 
 * 性能优化说明：
 * 1. useTranslation Hook 已经订阅了语言变化事件，只在语言真正变化时触发重新渲染
 * 2. useMemo 缓存计算结果，避免在同一个渲染周期内重复计算
 * 3. 依赖数组直接使用 i18n.resolvedLanguage 和 i18n.language，它们是稳定的字符串值
 * 4. 计算逻辑非常轻量（字符串处理和布尔判断），即使不缓存影响也很小
 * 
 * 推荐在 React 组件中使用此 Hook 替代 shouldUseVerticalLayout 函数
 * 
 * @returns 如果是非中文返回 true，否则返回 false
 * 
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const useVerticalLayout = useShouldUseVerticalLayout();
 *   
 *   return (
 *     <div className={useVerticalLayout ? 'flex-col' : 'flex-row'}>
 *       ...
 *     </div>
 *   );
 * };
 * ```
 */
export function useShouldUseVerticalLayout(): boolean {
    const { i18n } = useTranslation();
    
    // 使用 useMemo 缓存计算结果，只在语言变化时重新计算
    // i18n.resolvedLanguage 和 i18n.language 是字符串，只有当语言真正改变时才会变化
    // useTranslation Hook 已经处理了语言变化的订阅，所以这里只需要缓存计算结果即可
    return useMemo(() => {
        const lang = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().split('-')[0];
        return lang !== 'zh';
    }, [i18n.resolvedLanguage, i18n.language]);
}

/**
 * React Hook: 获取筛选器标签的样式类名
 * 根据语言自动返回合适的样式类名，统一处理筛选器标签的布局和对齐方式
 * 
 * @param spanClassName 额外的自定义样式类名（可选）
 * @returns 包含完整样式类名的字符串
 * 
 * @example
 * ```tsx
 * const MyFilter = () => {
 *   const labelClassName = useFilterLabelClassName();
 *   
 *   return (
 *     <div className="flex items-center">
 *       <span className={labelClassName}>标签</span>
 *       <input />
 *     </div>
 *   );
 * };
 * ```
 */
export function useFilterLabelClassName(spanClassName?: string): string {
    const useVerticalLayout = useShouldUseVerticalLayout();
    
    return useMemo(() => {
        // 如果 spanClassName 中包含 !font-bold 或 !font- 相关的类，则不添加默认的 font-semibold
        const hasFontOverride = spanClassName && (spanClassName.includes('!font-bold') || spanClassName.includes('!font-'));
        const baseClasses = hasFontOverride 
            ? 'text-gray-800 whitespace-nowrap' 
            : 'text-gray-800 whitespace-nowrap font-semibold';
        
        if (useVerticalLayout) {
            // 垂直布局（外语）：标签在上，左对齐
            return `${baseClasses} mb-1 text-left text-sm ${spanClassName || ''}`.trim();
        } else {
            // 水平布局（中文）：标签在左，左对齐，统一最小宽度，确保行高一致
            // 如果 spanClassName 中包含 !mr- 或 margin 相关的类，则不添加默认的 mr-2
            const hasMarginOverride = spanClassName && (spanClassName.includes('!mr-') || spanClassName.includes('mr-'));
            const defaultMargin = hasMarginOverride ? '' : 'mr-2';
            // 如果 spanClassName 中包含 !w-[ 或固定宽度相关的类，则不添加默认的 min-w-20
            const hasWidthOverride = spanClassName && (spanClassName.includes('!w-[') || spanClassName.includes('w-['));
            const defaultMinWidth = hasWidthOverride ? '' : 'min-w-20';
            return `${baseClasses} ${defaultMargin} text-left ${defaultMinWidth} leading-8 ${spanClassName || ''}`.trim();
        }
    }, [useVerticalLayout, spanClassName]);
}

/**
 * React Hook: 获取筛选器容器的样式类名
 * 根据语言自动返回合适的容器布局样式类名
 * 
 * @param className 额外的自定义样式类名（可选）
 * @param allowWrap 是否允许换行（默认 false，用于日期选择器等可能需要换行的场景）
 * @returns 包含完整样式类名的字符串
 * 
 * @example
 * ```tsx
 * const MyFilter = () => {
 *   const containerClassName = useFilterContainerClassName();
 *   
 *   return (
 *     <div className={containerClassName}>
 *       <span>标签</span>
 *       <input />
 *     </div>
 *   );
 * };
 * ```
 */
export function useFilterContainerClassName(className?: string, allowWrap: boolean = false): string {
    const useVerticalLayout = useShouldUseVerticalLayout();
    
    return useMemo(() => {
        if (useVerticalLayout) {
            // 垂直布局（外语）
            return `flex flex-col ${className || ''}`.trim();
        } else {
            // 水平布局（中文）：使用 items-center 确保标签和输入框垂直居中对齐
            const wrapClass = allowWrap ? 'flex-wrap' : '';
            return `flex items-center ${wrapClass} ${className || ''}`.trim();
        }
    }, [useVerticalLayout, className, allowWrap]);
}

/**
 * React Hook: 获取筛选器输入框容器的样式类名
 * 根据语言自动返回合适的输入框容器样式类名
 * 
 * @returns 样式类名字符串
 * 
 * @example
 * ```tsx
 * const MyFilter = () => {
 *   const inputContainerClassName = useFilterInputContainerClassName();
 *   
 *   return (
 *     <div className="flex items-center">
 *       <span>标签</span>
 *       <div className={inputContainerClassName}>
 *         <input />
 *       </div>
 *     </div>
 *   );
 * };
 * ```
 */
export function useFilterInputContainerClassName(): string {
    const useVerticalLayout = useShouldUseVerticalLayout();
    
    return useMemo(() => {
        // 添加 flex items-center 确保内容垂直居中
        // 统一带清空按钮的 Input (affix wrapper) 高度为 32px，与 Select 一致
        // 排除 TextArea（独占一行/多行），避免影响运单号等大输入框高度
        const affixHeightClass = [
            '[&_.ant-input-affix-wrapper:not(.ant-input-affix-wrapper-textarea)]:!h-8',
            '[&_.ant-input-affix-wrapper:not(.ant-input-affix-wrapper-textarea)]:!py-0',
            '[&_.ant-input-affix-wrapper:not(.ant-input-affix-wrapper-textarea)>input]:!h-8',
            '[&_.ant-input-affix-wrapper:not(.ant-input-affix-wrapper-textarea)>input]:!leading-8'
        ].join(' ');

        // 统一 Select / DatePicker 的内容垂直居中（不强制高度，避免影响多选 tag 自动换行）
        const selectAndPickerAlignClass = [
            // Select：让 selector/placeholder/selection item 垂直居中
            '[&_.ant-select-selector]:flex',
            '[&_.ant-select-selector]:items-center',
            '[&_.ant-select-selection-placeholder]:flex',
            '[&_.ant-select-selection-placeholder]:items-center',
            '[&_.ant-select-selection-item]:flex',
            '[&_.ant-select-selection-item]:items-center',
            // DatePicker/RangePicker：让输入区域垂直居中
            '[&_.ant-picker]:flex',
            '[&_.ant-picker]:items-center',
            '[&_.ant-picker-input]:flex',
            '[&_.ant-picker-input]:items-center'
        ].join(' ');

        const common = `${affixHeightClass} ${selectAndPickerAlignClass}`.trim();
        return useVerticalLayout
            ? `w-full ${common}`.trim()
            : `flex-1 min-w-0 flex items-center ${common}`.trim();
    }, [useVerticalLayout]);
}

/**
 * React Hook: 获取筛选器布局样式类名（支持强制水平布局）
 * 根据语言和 forceHorizontal 参数自动返回合适的布局样式类名
 * 
 * @param spanClassName 标签的额外样式类名（可选）
 * @param className 容器的额外样式类名（可选）
 * @param forceHorizontal 是否强制水平布局（可选，如果为 true 则无论语言都使用水平布局）
 * @returns 包含 containerClassName、labelClassName、inputContainerClassName 的对象
 * 
 * @example
 * ```tsx
 * const MyFilter = () => {
 *   const { containerClassName, labelClassName, inputContainerClassName } = useFilterLayoutStyles('w-[7em]', undefined, true);
 *   
 *   return (
 *     <div className={containerClassName}>
 *       <span className={labelClassName}>标签</span>
 *       <div className={inputContainerClassName}>
 *         <input />
 *       </div>
 *     </div>
 *   );
 * };
 * ```
 */
export function useFilterLayoutStyles(
    spanClassName?: string,
    className?: string,
    forceHorizontal?: boolean
): {
    containerClassName: string;
    labelClassName: string;
    inputContainerClassName: string;
} {
    const useVerticalLayout = useShouldUseVerticalLayout();
    const shouldUseVertical = forceHorizontal === true ? false : useVerticalLayout;
    
    // 复用现有的 Hook 获取默认样式
    const defaultLabelClassName = useFilterLabelClassName(spanClassName);
    const defaultInputContainerClassName = useFilterInputContainerClassName();
    
    // 根据布局方向计算样式类名
    const containerClassName = useMemo(() => {
        if (shouldUseVertical) {
            return `flex flex-col ${className || ''}`.trim();
        } else {
            return `flex items-center ${className || ''}`.trim();
        }
    }, [shouldUseVertical, className]);
    
    const labelClassName = useMemo(() => {
        if (shouldUseVertical) {
            // 垂直布局（外语）：标签在上
            const baseClasses = 'text-gray-800 whitespace-nowrap font-semibold';
            return `${baseClasses} mb-1 text-left text-sm ${spanClassName || ''}`.trim();
        } else {
            // 水平布局（中文）：标签在左，左对齐，支持换行
            const baseClasses = 'text-gray-500';
            const hasMarginOverride = spanClassName && (spanClassName.includes('!mr-') || spanClassName.includes('mr-'));
            const defaultMargin = hasMarginOverride ? '' : 'mr-2';
            return `${baseClasses} ${defaultMargin} text-left whitespace-normal ${spanClassName || ''}`.trim();
            // return `${baseClasses} ${defaultMargin} text-left whitespace-normal leading-8 ${spanClassName || ''}`.trim();
        }
    }, [shouldUseVertical, spanClassName]);
    
    const inputContainerClassName = useMemo(() => {
        // 如果布局方向与 Hook 返回的布局方向一致，直接使用 Hook 返回的样式
        // 否则，替换布局部分
        if (shouldUseVertical === useVerticalLayout) {
            return defaultInputContainerClassName;
        } else if (shouldUseVertical) {
            // 需要垂直布局，但 Hook 返回的是水平布局，替换布局部分
            return defaultInputContainerClassName.replace(/flex-1 min-w-0 flex items-center/, 'w-full');
        } else {
            // 需要水平布局，但 Hook 返回的是垂直布局，替换布局部分
            return defaultInputContainerClassName.replace(/w-full/, 'flex-1 min-w-0 flex items-center');
        }
    }, [shouldUseVertical, useVerticalLayout, defaultInputContainerClassName]);
    
    return {
        containerClassName,
        labelClassName,
        inputContainerClassName
    };
}

